import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { getRole } from '../../utils/authUtils';
import { eventNameFor } from '../../utils/eventName';

const REDIRECT_SECONDS = 5;

// The end of the signup flow, as its own route rather than a 'done' step
// inside AttendeeSignup. Two reasons, and the second is the one that matters:
//
// 1. It is a real destination - the account exists, the person is logged in,
//    and reloading or bookmarking it should not fall back to "this link is no
//    longer valid" (which is what the consumed ?token= signup URL does).
// 2. Marketing tracks signup as a *URL* conversion: the LinkedIn Insight Tag
//    (and anything else URL-based) matches on a page visit to a configured
//    address, so the flow has to actually arrive somewhere. The tag reports
//    client-side route changes as page visits - it wraps history.pushState and
//    replaceState and treats the resulting change like any other navigation -
//    so navigating here is enough; no per-conversion id or vendor setup in
//    this codebase. See trackSignup in utils/analytics.js.
//
// Reached with the event name in router state (set by AttendeeSignup). A
// direct visit or a reload has no state, which is fine: a page that only works
// when you arrive from the right place would be a poor thank-you page, and the
// conversion is the page visit itself.
const PageContainer = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
`;

const Card = styled.div`
  max-width: 480px;
  width: 100%;
  background-color: white;
  padding: 2rem;
  border-radius: 0px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  text-align: center;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 1rem;
`;

const RedirectNotice = styled.p`
  margin-top: 1rem;
  margin-bottom: 0;
  color: #6c757d;
  font-size: 0.9rem;
`;

const SignupSuccess = () => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const { state } = useLocation();
  const navigate = useNavigate();
  const registeredEventName = eventNameFor(intl.locale, state?.registeredEventName, state?.registeredEventNameEN) || null;
  const loggedIn = !!getRole();
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  // Auto-advance to the profile once the person is actually signed in.
  // Not attempted when logged out (no session was created here, e.g. a
  // reload/bookmark of this page) - there is nowhere useful to send them.
  useEffect(() => {
    if (!loggedIn) return undefined;

    if (secondsLeft <= 0) {
      navigate('/profile', { replace: true });
      return undefined;
    }

    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [loggedIn, secondsLeft, navigate]);

  return (
    <PageContainer>
      <Card>
        <SuccessMessage>
          <p>{t('signup.done', 'You\'re all set!')}</p>
          {registeredEventName && (
            <p>{t('verify.eventRegistered', 'You\'re now registered for {eventName}.', { eventName: registeredEventName })}</p>
          )}
        </SuccessMessage>
        <p style={{ marginTop: '1rem', marginBottom: 0 }}>
          <Link to={loggedIn ? '/profile' : '/login'}>
            {loggedIn ? t('nav.myProfile', 'Go to my profile') : t('auth.signIn', 'Sign in')}
          </Link>
        </p>
        {loggedIn && (
          <RedirectNotice>
            {t('signup.redirecting', 'Taking you to your profile in {seconds}…', { seconds: secondsLeft })}
          </RedirectNotice>
        )}
      </Card>
    </PageContainer>
  );
};

export default SignupSuccess;
